/** @jsxImportSource @emotion/react */
import { Buttons } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { theme } from 'global';
import {
  ChangeEvent,
  DragEvent,
  ForwardedRef,
  forwardRef,
  JSX,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_ACCEPT,
  DELETE_CONFIRMATION_CANCEL,
  DELETE_MODAL_CLOSE_DURATION_MS,
  DELETE_CONFIRMATION_CONFIRM,
  DELETE_CONFIRMATION_TITLE,
  FILE_LOAD_TIMEOUT_MS,
  FILE_PROGRESS_ANIMATION_DELAY_MS,
} from './GuidedFileUpload.constants';
import {
  GuidedFileUploaderProps,
  GuidedFileUploaderRef,
  GuidedFileUploaderStage,
} from './GuidedFileUploaderProps';
import * as styles from './GuidedFileUpload.styles';

interface FileEntry {
  file: File;
  id: string;
  progress: number;
  status: 'loading' | 'loaded';
}

const GuidedFileUploader = (
  {
    requirements,
    onVerify,
    accept = DEFAULT_ACCEPT,
    disabled = false,
    multiple = true,
    maxFiles,
    onSingleFileReplaceAttempt,
    onMaxFilesAttempt,
    onFilesChange,
    initialFiles,
    initialRequirementResults,
  }: GuidedFileUploaderProps,
  ref: ForwardedRef<GuidedFileUploaderRef>
): JSX.Element => {
  const [fileEntries, setFileEntries] = useState<FileEntry[]>(() =>
    (initialFiles ?? []).map((file, index) => ({
      file,
      id: `restore-${file.name}-${file.size}-${file.lastModified}-${index}`,
      progress: 100,
      status: 'loaded' as const,
    }))
  );
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [requirementResults, setRequirementResults] = useState<
    boolean[] | null
  >(() => initialRequirementResults ?? null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeleteModalClosing, setIsDeleteModalClosing] =
    useState<boolean>(false);
  const [deleteModalLayout, setDeleteModalLayout] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loadingTimeoutsRef = useRef<Record<string, number>>({});
  const deleteModalCloseTimeoutRef = useRef<number | null>(null);

  const loadedFiles = useMemo(
    () =>
      fileEntries
        .filter((entry) => entry.status === 'loaded')
        .map((entry) => entry.file),
    [fileEntries]
  );
  const hasLoadingFiles = fileEntries.some(
    (entry) => entry.status === 'loading'
  );

  let currentStage: GuidedFileUploaderStage = 'loaded';

  if (fileEntries.length === 0) {
    currentStage = 'empty';
  } else if (hasLoadingFiles) {
    currentStage = 'loading';
  } else if (isVerifying) {
    currentStage = 'verifying';
  } else if (requirementResults) {
    currentStage = requirementResults.every(Boolean)
      ? 'uploaded-success'
      : 'uploaded-error';
  }

  const clearFileTimeout = (fileId: string): void => {
    if (loadingTimeoutsRef.current[fileId]) {
      window.clearTimeout(loadingTimeoutsRef.current[fileId]);
      delete loadingTimeoutsRef.current[fileId];
    }
  };

  const clearDeleteModalCloseTimeout = (): void => {
    if (deleteModalCloseTimeoutRef.current) {
      window.clearTimeout(deleteModalCloseTimeoutRef.current);
      deleteModalCloseTimeoutRef.current = null;
    }
  };

  const closeDeleteModal = (): void => {
    setIsDeleteModalClosing(true);
    clearDeleteModalCloseTimeout();
    deleteModalCloseTimeoutRef.current = window.setTimeout(() => {
      setPendingDeleteId(null);
      setIsDeleteModalClosing(false);
      deleteModalCloseTimeoutRef.current = null;
    }, DELETE_MODAL_CLOSE_DURATION_MS);
  };

  const updateDeleteModalLayout = (): void => {
    if (!rootRef.current) {
      return;
    }

    const rect = rootRef.current.getBoundingClientRect();
    const isMobileViewport = window.innerWidth <= theme.breaks.xs;

    setDeleteModalLayout({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width / 2,
      width: isMobileViewport ? rect.width : 520,
    });
  };

  const removeFile = (fileId: string): void => {
    clearFileTimeout(fileId);
    clearDeleteModalCloseTimeout();
    setIsDeleteModalClosing(false);
    setRequirementResults(null);
    setPendingDeleteId(null);
    setFileEntries((currentEntries) =>
      currentEntries.filter((entry) => entry.id !== fileId)
    );
  };

  const queueFiles = (incomingFiles: File[]): void => {
    if (!incomingFiles.length || disabled) {
      return;
    }

    if (!multiple && fileEntries.length > 0 && onSingleFileReplaceAttempt) {
      onSingleFileReplaceAttempt?.();
      return;
    }

    if (
      maxFiles !== undefined &&
      fileEntries.length >= maxFiles &&
      onMaxFilesAttempt
    ) {
      onMaxFilesAttempt();
      return;
    }

    setRequirementResults(null);

    const queuedEntries = incomingFiles.map((file, index) => ({
      file,
      id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${index}`,
      progress: 0,
      status: 'loading' as const,
    }));

    setFileEntries((currentEntries) => {
      if (!multiple) {
        Object.keys(loadingTimeoutsRef.current).forEach(clearFileTimeout);
        return queuedEntries.slice(0, 1);
      }

      if (maxFiles !== undefined) {
        const remainingSlots = maxFiles - currentEntries.length;
        if (remainingSlots <= 0) {
          return currentEntries;
        }
        return [...currentEntries, ...queuedEntries.slice(0, remainingSlots)];
      }

      return [...currentEntries, ...queuedEntries];
    });

    window.setTimeout(() => {
      setFileEntries((currentEntries) =>
        currentEntries.map((entry) =>
          queuedEntries.some((queuedEntry) => queuedEntry.id === entry.id)
            ? { ...entry, progress: 100 }
            : entry
        )
      );

      queuedEntries.forEach((queuedEntry) => {
        loadingTimeoutsRef.current[queuedEntry.id] = window.setTimeout(() => {
          delete loadingTimeoutsRef.current[queuedEntry.id];
          setFileEntries((current) =>
            current.map((e) =>
              e.id === queuedEntry.id && e.status === 'loading'
                ? { ...e, status: 'loaded' }
                : e
            )
          );
        }, FILE_LOAD_TIMEOUT_MS);
      });
    }, FILE_PROGRESS_ANIMATION_DELAY_MS);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const incomingFiles = Array.from(event.target.files ?? []);
    queueFiles(incomingFiles);
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (!disabled && !isVerifying) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    queueFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const openFileDialog = (): void => {
    if (!disabled && !isVerifying) {
      inputRef.current?.click();
    }
  };

  const handleDropzoneKeyDown = (
    event: KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFileDialog();
    }
  };

  const normalizeResults = (results: boolean[]): boolean[] =>
    requirements.map((_, index) => Boolean(results[index]));

  const verify = async (): Promise<boolean[] | null> => {
    if (
      disabled ||
      isVerifying ||
      hasLoadingFiles ||
      loadedFiles.length === 0
    ) {
      return null;
    }

    setIsVerifying(true);

    try {
      const verificationResults = normalizeResults(await onVerify(loadedFiles));
      setRequirementResults(verificationResults);
      return verificationResults;
    } finally {
      setIsVerifying(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      verify,
    }),
    [verify]
  );

  useEffect(() => {
    onFilesChange?.(loadedFiles);
  }, [loadedFiles, onFilesChange]);

  useEffect(() => {
    if (!pendingDeleteId) {
      setDeleteModalLayout(null);
      return;
    }

    updateDeleteModalLayout();

    const handleViewportChange = (): void => {
      updateDeleteModalLayout();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return (): void => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [pendingDeleteId]);

  useEffect(() => {
    return (): void => {
      Object.keys(loadingTimeoutsRef.current).forEach(clearFileTimeout);
      clearDeleteModalCloseTimeout();
    };
  }, []);

  const renderRequirementIcon = (index: number): JSX.Element => {
    if (
      currentStage === 'uploaded-error' ||
      currentStage === 'uploaded-success'
    ) {
      const isRequirementMet = requirementResults?.[index];

      if (isRequirementMet) {
        return (
          <Icons.CheckCircle
            width="20"
            height="20"
            fill={theme.colors.success}
            aria-label="Success"
          />
        );
      }

      return (
        <Icons.WarningCircle
          width="20"
          height="20"
          fill={theme.colors.danger}
          aria-label="Warning"
        />
      );
    }

    return <span css={styles.dot} />;
  };

  return (
    <div ref={rootRef} css={styles.root}>
      {Boolean(pendingDeleteId) && (
        <div
          css={styles.deleteModalOverlay(isDeleteModalClosing)}
          onClick={closeDeleteModal}
        >
          <div
            css={styles.deleteModalPositioner}
            style={deleteModalLayout ?? undefined}
          >
            <div
              css={styles.deleteModal(isDeleteModalClosing)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="guidedFileUploadDeleteDialogTitle"
              onClick={(event): void => event.stopPropagation()}
            >
              <div css={styles.deleteModalHeader}>
                <div
                  id="guidedFileUploadDeleteDialogTitle"
                  className="heading1"
                  css={styles.deleteModalTitle}
                >
                  {DELETE_CONFIRMATION_TITLE}
                </div>
              </div>
              <div css={styles.deleteModalActions}>
                <Buttons.Text
                  onClick={closeDeleteModal}
                  css={styles.deleteModalCancel}
                >
                  {DELETE_CONFIRMATION_CANCEL}
                </Buttons.Text>
                <Buttons.Primary
                  onClick={(): void => {
                    if (pendingDeleteId) {
                      removeFile(pendingDeleteId);
                    }
                  }}
                  css={styles.dialogDeleteButton}
                >
                  {DELETE_CONFIRMATION_CONFIRM}
                </Buttons.Primary>
              </div>
            </div>
          </div>
        </div>
      )}

      <div css={styles.content}>
        <div>
          <p className="body1" css={styles.title}>
            Requirements
          </p>
          <ul css={styles.requirementsList}>
            {requirements.map((requirement, index) => (
              <li key={`${requirement}-${index}`} css={styles.requirementItem}>
                <span css={styles.requirementIcon}>
                  {renderRequirementIcon(index)}
                </span>
                <p className="body4" css={styles.requirementText}>
                  {requirement}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div
          css={styles.dropzone(isDragging, disabled || isVerifying)}
          onClick={openFileDialog}
          onKeyDown={handleDropzoneKeyDown}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={disabled || isVerifying ? -1 : 0}
          aria-disabled={disabled || isVerifying}
        >
          <input
            ref={inputRef}
            css={styles.hiddenInput}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleInputChange}
            disabled={disabled || isVerifying}
          />
          <div css={styles.uploadIcon}>
            <Icons.ArrowUpload
              fill={theme.colors.primary.medium}
              width="32"
              height="32"
            />
          </div>
          <div css={styles.dropzoneTextGroup}>
            <p className="body1" css={styles.browseText}>
              Browse your device
            </p>
            <p className="body2" css={styles.dropText}>
              or drag and drop
            </p>
          </div>
        </div>

        <button
          type="button"
          css={styles.mobileUploadButton(disabled || isVerifying)}
          onClick={openFileDialog}
          disabled={disabled || isVerifying}
        >
          <p className="body1" css={styles.mobileUploadText}>
            Upload file
          </p>
        </button>

        {fileEntries.length > 0 && (
          <>
            <hr css={styles.divider} />
            <ul css={styles.fileList}>
              {fileEntries.map((entry) => {
                const isLoaded = entry.status === 'loaded';

                return (
                  <li key={entry.id} css={styles.fileRow(isLoaded)}>
                    <div
                      data-testid="file-loading-progress"
                      css={styles.fileProgress(entry.progress)}
                      onTransitionEnd={() => {
                        if (
                          entry.status === 'loading' &&
                          entry.progress === 100
                        ) {
                          clearFileTimeout(entry.id);
                          setFileEntries((current) =>
                            current.map((e) =>
                              e.id === entry.id ? { ...e, status: 'loaded' } : e
                            )
                          );
                        }
                      }}
                    />
                    <div css={styles.fileContent}>
                      <Icons.Statement
                        width="20"
                        height="20"
                        fill={theme.colors.neutral[600]}
                      />
                      <p className="body2" css={styles.fileName}>
                        {entry.file.name}
                      </p>
                      <button
                        type="button"
                        css={styles.iconButton(
                          isLoaded &&
                            (currentStage === 'uploaded-error' ||
                              currentStage === 'uploaded-success')
                        )}
                        aria-label={
                          isLoaded
                            ? `Delete ${entry.file.name}`
                            : `Cancel ${entry.file.name}`
                        }
                        onClick={(event) => {
                          event.stopPropagation();

                          if (isLoaded) {
                            clearDeleteModalCloseTimeout();
                            setIsDeleteModalClosing(false);
                            setPendingDeleteId(entry.id);
                            return;
                          }

                          removeFile(entry.id);
                        }}
                        disabled={isVerifying}
                      >
                        {isLoaded ? (
                          <Icons.Trash
                            width="20"
                            height="20"
                            fill={theme.colors.danger}
                          />
                        ) : (
                          <Icons.Close
                            width="20"
                            height="20"
                            fill={theme.colors.primary.medium}
                          />
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default forwardRef(GuidedFileUploader);
